import { useState, useEffect } from 'react';
import { getAuth, updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { sendVerificationCode, verifyPhoneNumber, updateUserPhoneNumber } from '../../auth/authService';

const useProfileEdit = (userData, onClose, onSuccess) => {
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (userData) {
      setDisplayName(userData.displayName || '');
      setPhoneNumber(userData.phoneNumber || '');
      setIsPhoneVerified(!!userData.phoneNumber);
    }
  }, [userData]);

  // Check if the phone number is valid
  const isValidPhoneNumber = (phone) => {
    // Enhanced validation for international phone numbers
    if (!phone || phone.trim() === '') return false;
    
    // Basic format validation
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/[\s-]/g, ''));
  };

  const formatPhoneNumber = (phone) => {
    // Ensure phone starts with + for international format if it starts with numbers
    if (/^[0-9]/.test(phone)) {
      // Handle Israeli numbers specifically
      if (phone.startsWith('05') || phone.startsWith('07')) {
        return '+972' + phone.substring(1);
      } else if (phone.startsWith('0')) {
        return '+972' + phone.substring(1);
      }
      return '+' + phone;
    }
    return phone;
  };

  const handleSendVerificationCode = async () => {
    // Reset any previous errors
    setPhoneError('');
    setSuccessMessage('');
    
    // Format and validate phone number
    const formattedPhone = formatPhoneNumber(phoneNumber);
    setPhoneNumber(formattedPhone);
    
    if (!isValidPhoneNumber(formattedPhone)) {
      setPhoneError('Please enter a valid phone number (e.g. +972541234567)');
      return;
    }

    try {
      setIsSendingCode(true);
      
      // Ensure the recaptcha element exists and is ready
      const recaptchaContainer = document.getElementById('recaptcha-container');
      if (!recaptchaContainer) {
        console.error('reCAPTCHA container not found');
        setPhoneError('Error loading reCAPTCHA. Please refresh the page and try again.');
        return;
      }
      
      // Clear any existing content
      recaptchaContainer.innerHTML = '';
      
      // Add a loading indicator to the container
      const loadingElement = document.createElement('div');
      loadingElement.className = 'flex items-center justify-center h-full w-full';
      loadingElement.innerHTML = `
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <span class="ml-2 text-gray-400">Loading reCAPTCHA...</span>
      `;
      recaptchaContainer.appendChild(loadingElement);
      
      // Send verification code to the phone number
      const id = await sendVerificationCode(formattedPhone, 'recaptcha-container');
      setVerificationId(id);
      setShowPhoneVerification(true);
      setSuccessMessage('Verification code sent successfully');

    } catch (error) {
      console.error('Error sending verification code:', error);
      
      // Provide detailed error messages based on error type
      if (error.code === 'auth/invalid-phone-number') {
        setPhoneError('Invalid phone number. Please enter a number with country code (e.g. +972541234567)');
      } else if (error.code === 'auth/captcha-check-failed') {
        setPhoneError('reCAPTCHA verification failed. Please refresh the page and try again.');
      } else if (error.code === 'auth/quota-exceeded') {
        setPhoneError('You have exceeded the request quota. Please try again later.');
      } else if (error.code === 'auth/missing-verification-code' || error.code === 'auth/missing-verification-id') {
        setPhoneError('Some verification data is missing. Please refresh the page and try again.');
      } else if (error.code === 'auth/app-not-authorized') {
        setPhoneError('This app is not authorized for SMS verification. Please contact the system administrator.');
      } else if (error.code === 'auth/web-context-cancelled') {
        setPhoneError('Verification operation was cancelled. Please try again.');
      } else if (error.code === 'auth/network-request-failed') {
        setPhoneError('Failed to connect to the server. Please check your internet connection and try again.');
      } else if (error.message && error.message.includes('timed out')) {
        setPhoneError('Verification request timed out. Please try again.');
      } else {
        setPhoneError('Error sending verification code. Please refresh the page and try again.');
        
        // Try to clear the recaptcha on error
        try {
          if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear();
            window.recaptchaVerifier = null;
          }
          
          // Get the recaptcha container element
          const recaptchaElement = document.getElementById('recaptcha-container');
          if (recaptchaElement) {
            recaptchaElement.innerHTML = '';
          }
        } catch (e) {
          console.error('Error clearing reCAPTCHA:', e);
        }
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    // Reset any previous errors
    setVerificationError('');
    setSuccessMessage('');
    
    if (!verificationCode || verificationCode.trim() === '') {
      setVerificationError('Please enter the verification code');
      return;
    }

    try {
      setIsVerifying(true);
      
      // Verify the code entered by the user
      if (!userData.phoneNumber) {
        // Link phone number to account if it doesn't exist
        await verifyPhoneNumber(verificationId, verificationCode);
      } else {
        // Update phone number if it already exists
        await updateUserPhoneNumber(verificationId, verificationCode);
      }
      
      setIsPhoneVerified(true);
      setSuccessMessage('Phone number verified successfully');
      setShowPhoneVerification(false);
    } catch (error) {
      console.error('Error verifying code:', error);
      
      // Provide specific error messages based on error type
      if (error.code === 'auth/invalid-verification-code') {
        setVerificationError('Invalid verification code. Please try again.');
      } else if (error.code === 'auth/code-expired') {
        setVerificationError('Verification code has expired. Please request a new code.');
      } else if (error.code === 'auth/credential-already-in-use') {
        setVerificationError('This phone number is already associated with another account.');
      } else if (error.code === 'auth/invalid-verification-id') {
        setVerificationError('Invalid verification ID. Please request a new verification code.');
      } else {
        setVerificationError('Error verifying code. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');
    setLoading(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('No user is currently signed in');
      }

      // Update display name in Firebase Auth
      if (displayName !== userData.displayName) {
        await updateProfile(user, { displayName });
      }

      // Update user document in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        phoneNumber: isPhoneVerified ? phoneNumber : userData.phoneNumber || '',
        updatedAt: new Date()
      });

      setSuccessMessage('Profile updated successfully');
      
      // Notify parent component
      if (onSuccess) {
        onSuccess({
          ...userData,
          phoneNumber: isPhoneVerified ? phoneNumber : userData.phoneNumber || ''
        });
      }
      
      // Close modal after success
      if (onClose && !phoneError && !verificationError) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Error updating profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle phone number change to reset verification if needed
  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(value);
    
    // If phone number changes and was previously verified, reset verification
    if (isPhoneVerified && value !== userData.phoneNumber) {
      setIsPhoneVerified(false);
      setShowPhoneVerification(false);
    }
    
    // Clear previous error when typing
    if (phoneError) {
      setPhoneError('');
    }
  };

  // Clear recaptcha and reset verification state
  const resetVerification = () => {
    setShowPhoneVerification(false);
    setVerificationId('');
    setVerificationCode('');
    setPhoneError('');
    setVerificationError('');
    
    // Clear reCAPTCHA
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      } catch (e) {
        console.error('Error clearing reCAPTCHA:', e);
      }
    }
    
    const recaptchaElement = document.getElementById('recaptcha-container');
    if (recaptchaElement) {
      recaptchaElement.innerHTML = '';
    }
  };

  return {
    displayName,
    setDisplayName,
    phoneNumber,
    setPhoneNumber: handlePhoneNumberChange,
    isPhoneVerified,
    setIsPhoneVerified,
    showPhoneVerification,
    verificationCode,
    setVerificationCode,
    loading,
    error,
    successMessage,
    phoneError,
    verificationError,
    isSendingCode,
    isVerifying,
    handleSendVerificationCode,
    handleVerifyCode,
    handleSubmit,
    resetVerification
  };
};

export default useProfileEdit; 